import { requireRuntimeValue, runtimeConfig } from "./runtimeConfig";
import { withRequestDeadline } from "../utils/requestDeadline";

export const REFRESH_TOKEN_STORAGE_KEY = "refresh_token";
export const ACCESS_TOKEN_STORAGE_KEY = "access_token";
export const REFRESH_TOKEN_EXPIRY_STORAGE_KEY = "refresh_token_exp";
export const TOKEN_REFRESH_LEAD_TIME = 5 * 60 * 1000;
let refreshInFlight: Promise<RefreshTokenResponse> | null = null;

export const getExpiryFromToken = (
  token?: string | null,
): number | undefined => {
  if (!token) {
    return undefined;
  }

  try {
    const payload = token.split(".")[1];
    if (!payload) {
      return undefined;
    }

    const decodedPayload = globalThis.atob(
      payload
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .padEnd(Math.ceil(payload.length / 4) * 4, "="),
    );
    const expiry = Number(
      (JSON.parse(decodedPayload) as { exp?: number | string }).exp,
    );
    return Number.isFinite(expiry) ? expiry : undefined;
  } catch {
    return undefined;
  }
};

interface RefreshTokenResponse {
  access_token: string;
  token_type: string;
  expires_in?: number;
  refresh_expires_in?: number;
  refresh_token: string;
  scope?: string;
  [key: string]: unknown;
}

async function refreshToken(): Promise<RefreshTokenResponse> {
  const storedRefreshToken = sessionStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  const clientId = runtimeConfig.oidcClientId;
  const authority = requireRuntimeValue(
    "VITE_OIDC_AUTHORITY",
    runtimeConfig.oidcAuthority,
  );

  if (!storedRefreshToken) {
    throw new Error("No refresh token found in session storage");
  }

  const configuredClientId = requireRuntimeValue(
    "VITE_OIDC_CLIENT_ID",
    clientId,
  );

  const payload = new URLSearchParams({
    refresh_token: storedRefreshToken,
    client_id: configuredClientId,
    grant_type: "refresh_token",
  });

  const response = await withRequestDeadline((signal) =>
    fetch(`${authority}/protocol/openid-connect/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: payload.toString(),
      signal,
    }),
  );

  if (!response.ok) {
    let providerError = "";
    try {
      const errorBody = (await response.json()) as {
        error?: string;
        error_description?: string;
      };
      providerError = [errorBody.error, errorBody.error_description]
        .filter(Boolean)
        .join(": ");
    } catch {
      providerError = await response.text().catch(() => "");
    }

    throw new Error(
      `Token refresh failed: ${response.status} ${response.statusText}${providerError ? ` - ${providerError}` : ""}`,
    );
  }

  const newToken = (await response.json()) as RefreshTokenResponse;

  sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, newToken.access_token);
  sessionStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, newToken.refresh_token);

  const expiry = getExpiryFromToken(newToken.refresh_token);
  if (expiry !== undefined) {
    sessionStorage.setItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY, String(expiry));
  }
  return newToken;
}

// Refresh must happen before either the access token or the refresh token expires.
export function getEarliestTokenExpiry(
  accessToken?: string | null,
): number | undefined {
  const refreshExpiry = Number(
    sessionStorage.getItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY),
  );
  const expiries = [
    getExpiryFromToken(accessToken),
    sessionStorage.getItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY)
      ? refreshExpiry
      : undefined,
  ].filter((value): value is number => Number.isFinite(value));
  return expiries.length ? Math.min(...expiries) : undefined;
}

export async function verifyToken(): Promise<string | null> {
  const accessToken = sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  if (!accessToken) {
    return null;
  }

  const expiry = getEarliestTokenExpiry(accessToken);
  if (expiry === undefined) {
    throw new Error("Token expiry is missing or invalid");
  }

  const isTokenExpired = expiry * 1000 - Date.now() <= TOKEN_REFRESH_LEAD_TIME;

  if (isTokenExpired) {
    refreshInFlight ??= refreshToken().finally(() => {
      refreshInFlight = null;
    });
    const refreshedToken = await refreshInFlight;
    return refreshedToken.access_token;
  }

  return accessToken;
}

// Refreshes on demand so a throttled or missed scheduled refresh cannot leave callers with an expired token.
export async function getToken(): Promise<string | null> {
  try {
    return await verifyToken();
  } catch {
    return null;
  }
}
